import { useEffect, useRef } from 'react';
import { connect, disconnect, resume } from '../socket/client';
import { registerHandlers, unregisterHandlers } from '../socket/handlers';
import useAuthStore from '../stores/authStore';

export default function useSocket() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const connectedRef = useRef(false);

  // Note: this deliberately does NOT depend on accessToken. The socket reads the token
  // from storage on every (re)connection attempt, so a silent refresh must not tear the
  // connection down and rebuild it every 15 minutes.
  useEffect(() => {
    if (isAuthenticated && !connectedRef.current) {
      connectedRef.current = true;
      connect();
      registerHandlers();
    }

    return () => {
      if (connectedRef.current) {
        unregisterHandlers();
        disconnect();
        connectedRef.current = false;
      }
    };
  }, [isAuthenticated]);

  // Waking a backgrounded tab is the failure case this whole file exists for: timers
  // were throttled, heartbeats were missed, the server dropped us, and the access token
  // expired while nobody was looking. Treat every wake signal as "go re-establish".
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const handleWake = () => {
      if (document.visibilityState === 'visible') {
        resume();
      }
    };

    document.addEventListener('visibilitychange', handleWake);
    window.addEventListener('focus', handleWake);
    window.addEventListener('online', handleWake);

    return () => {
      document.removeEventListener('visibilitychange', handleWake);
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('online', handleWake);
    };
  }, [isAuthenticated]);
}
